import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'How much does it cost to rent a car in Pakistan?',
    answer: 'Car rental prices at Amjed Tours start from Rs. 7,000 per day for a Toyota Corolla Grande (with driver) and go up to Rs. 25,000 per day for a Toyota Land Cruiser V8. Prices vary based on the vehicle and whether you choose self-drive or with-driver option.'
  },
  {
    question: 'Do you offer with-driver rentals?',
    answer: 'Yes! Amjed Tours offers both self-drive and with-driver options. For most vehicles, our with-driver rate is actually cheaper than self-drive since our drivers are experienced with Pakistani roads and reduce our operational risk. Professional chauffeur service is included.'
  },
  {
    question: 'What cars are available for rent in Islamabad?',
    answer: 'In Islamabad, we offer Toyota Land Cruiser V8, Toyota Prado, Toyota Hilux Revo, Honda Civic, and Toyota Corolla Grande. All vehicles are well-maintained and available for both city travel and northern areas trips.'
  },
  {
    question: 'Can I rent a car for Northern Areas trip (Hunza, Skardu, Naran)?',
    answer: 'Absolutely! Our Toyota Prado, Land Cruiser V8, and Hilux Revo are perfect for northern areas trips including Hunza, Skardu, Naran, Kaghan, and Karakoram Highway. We recommend booking with-driver option for mountain terrain.'
  },
  {
    question: 'What documents do I need to rent a car?',
    answer: 'For self-drive rentals, you need: (1) A valid Pakistani driving license or international license, (2) Original CNIC, (3) A security deposit. For with-driver rentals, only booking details are required.'
  },
  {
    question: 'How do I book a car with Amjed Tours?',
    answer: 'You can book online through our website, call us at 0311-9951299, or message us on WhatsApp. Simply choose your car, pick your dates, and confirm your booking. Instant confirmation via WhatsApp.'
  },
  {
    question: 'Do you provide cars for wedding events?',
    answer: 'Yes, we specialize in wedding car rentals. The Toyota Land Cruiser V8 and Prado are our most popular wedding cars, decorated on request. Book at least 2 weeks in advance for wedding dates.'
  },
  {
    question: 'Is fuel included in the rental price?',
    answer: 'Fuel is not included in the base rental price for self-drive rentals. The customer is responsible for fuel costs. For with-driver bookings, please discuss fuel arrangements with our team during booking.'
  },
  {
    question: 'What areas do you serve in Pakistan?',
    answer: 'Amjed Tours operates across Pakistan with major service in Islamabad, Rawalpindi, Lahore, Karachi, Peshawar, and all northern areas including Hunza, Skardu, Naran, Kaghan, Chitral, and Kalash Valley.'
  },
  {
    question: 'Can I extend my rental period?',
    answer: 'Yes, rental extensions are possible subject to car availability. Please contact us at least 24 hours before your current rental ends to request an extension. Additional daily rate applies.'
  }
];

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  // Inject FAQ schema for SEO
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    });
    script.id = 'faq-schema';
    if (!document.getElementById('faq-schema')) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="py-24 px-6 bg-muted" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.2em] mb-3 font-mono text-accent">Frequently Asked Questions</p>
          <h2 className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="faq-title">
            Got Questions? We Have Answers
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg border border-border overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors"
                data-testid={`faq-question-${index}`}
              >
                <span className="font-semibold pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <p className="p-5 pt-0 text-muted-foreground leading-relaxed" data-testid={`faq-answer-${index}`}>
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
